import React, {Component} from 'react';
import {connect} from 'react-redux';
import {reduxForm, Field} from 'redux-form';
import {Button, Card, Form, Loader, Icon} from 'semantic-ui-react';
import moment from 'moment';
import {dayClassification, pickingOrderClassification} from '../../../../../common/dictionaries/classification';
import {requiredArray, minTime, maxTime} from '../../../../../common/components/form/common/fieldValidations';
import {InputField, CheckboxField, DropdownField, TimeField} from '../../../../../common/components/form';
import {setErrorMessage, clearErrorMessage} from '../../../../../common/components/form/common';
import {hourFormat, originModificationDate} from '../../../../../common/constants/datesFormat';
import {hasAccessPermission} from '../../../../../common/permissions/validations';
import {resources, scopes} from '../../../../../common/permissions/dictionary';
import {parseDictionaryWithNumbers} from '../../../../../common/util/options';
import {paths} from '../../../../../common/dictionaries/urls';
import {SHIFT_FORM} from '../../../../../common/forms';
import {Actions} from '../../../../../common/actions';

const MIN_TIME_ALLOWED = '07:00';
const MAX_TIME_ALLOWED = '19:00';

const minThanMinTimeAllowed = minTime(MIN_TIME_ALLOWED, hourFormat);
const maxThanMaxTimeAllowed = maxTime(MAX_TIME_ALLOWED, hourFormat);

class ShiftForm extends Component {
  constructor(props) {
    super(props);
    this.onCancelClick = this.onCancelClick.bind(this);
  }

  componentDidMount() {
    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PickingShifts,
        scopes.See
      ) &&
      !hasAccessPermission(
        this.props.permissions,
        resources.PickingShifts,
        scopes.Edit
      )
    ) {
      this.props.push(paths.AccessDenied);
    }
    const {code} = this.props;
    if (code) {
      this.props.fetchShift(code);
    }
  }

  componentDidUpdate(prevProps) {
    const {code} = this.props;
    if (code !== prevProps.code) {
      this.props.fetchShift(code);
    }
  }

  onCancelClick() {
    this.props.goBack();
  }

  render() {
    const {handleSubmit, submitting, code} = this.props;

    return (
      <Form
        className='shift-form'
        onSubmit={handleSubmit}
        loading={submitting}
        autoComplete='off'
      >
        <Card raised fluid>
          <div className='title-bar'>
            <div className='title'>
              <Icon name='arrow left' onClick={this.onCancelClick} />
              <h2>{code ? 'Editar Corte' : 'Añadir Corte'}</h2>
              <div className='title-bar-actions'>
                <Button
                  type='button'
                  secondary
                  className='toyota-secondary'
                  onClick={this.onCancelClick}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                {this.renderSaveButton()}
              </div>
            </div>
          </div>
        </Card>

        <Card raised fluid>
          {this.renderFormFields()}
        </Card>
      </Form>
    );
  }

  renderSaveButton() {
    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PickingShifts,
        scopes.Edit
      )
    ) {
      return null;
    }
    const {submitting} = this.props;
    return (
      <Button primary type='submit' disabled={submitting}>
        Guardar
      </Button>
    );
  }

  renderFormFields() {
    const {loading} = this.props;

    if (loading) {
      return <Loader active inline='centered' />;
    }

    return (
      <div className='page-content'>
        <div className='page-row'>
          <div className='field-label'>
            <label>Nombre*</label>
          </div>
          <div className='field-component'>
            <Field
              name='name'
              component={InputField}
              type='text'
              onInvalid={(event) =>
                setErrorMessage('Por favor, llene este campo', event)
              }
              onInput={clearErrorMessage}
            />
          </div>
        </div>
        <div className='page-row'>
          <div className='field-label'>
            <label>Tipos de pedidos a los que aplica*</label>
          </div>
          <div className='field-component'>
            <Field
              name='orders'
              component={DropdownField}
              multiple
              options={parseDictionaryWithNumbers(pickingOrderClassification)}
              validate={requiredArray}
            />
          </div>
        </div>
        <div className='page-row'>
          <div className='times-grid'>
            <div className='emission-time-label'>
              <label>Hora de emisión de etiquetas*</label>
            </div>
            <div className='emission-time-component'>
              <Field
                name='labelsEmissionTime'
                component={TimeField}
                type='text'
                required
                validate={[minThanMinTimeAllowed, maxThanMaxTimeAllowed]}
              />
            </div>
            <div className='dispatch-time-label'>
              <label>Hora de despacho*</label>
            </div>
            <div className='dispatch-time-component'>
              <Field
                name='dispatchTime'
                component={TimeField}
                type='text'
                required
                validate={[minThanMinTimeAllowed, maxThanMaxTimeAllowed]}
              />
            </div>
          </div>
        </div>
        <div className='page-row'>
          <div className='field-label'>
            <label>Días a los que aplica*</label>
          </div>
          <div className='field-component'>
            <Field
              name='days'
              component={DropdownField}
              multiple
              options={parseDictionaryWithNumbers(dayClassification)}
              validate={requiredArray}
            />
          </div>
        </div>
        {this.renderStatusField()}
        {this.renderModificationDateField()}
      </div>
    );
  }

  renderStatusField() {
    const {code} = this.props;

    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PickingShifts,
        scopes.Disabled
      )
    ) {
      return null;
    }

    return (
      <div className='page-row'>
        <div className='field-label'>
          <label>Estado</label>
        </div>
        <div className='field-component'>
          <Field
            type='toggle'
            name='status'
            component={CheckboxField}
            disabled={
              !code ||
              !hasAccessPermission(
                this.props.permissions,
                resources.PickingShifts,
                scopes.Edit
              )
            }
          />
        </div>
      </div>
    );
  }

  renderModificationDateField() {
    const {code, modificationDate} = this.props;

    if (!code) {
      return null;
    }

    return (
      <div className='page-row'>
        <div className='field-label'>
          <label>Última Modificación</label>
        </div>
        <div className='field-component'>
          <p>{moment(modificationDate).format(originModificationDate)}</p>
        </div>
      </div>
    );
  }
}

export {ShiftForm};

const form = reduxForm({
  'form': SHIFT_FORM.name,
})(ShiftForm);

const mapStateToProps = (state, ownProps) => {
  return {
    'loading': state.form.shifts.loading,
    'modificationDate': state.shifts.modificationDate,
    'labelsEmissionTime': state.form.shifts.values.labelsEmissionTime,
    'dispatchTime': state.form.shifts.values.dispatchTime,
    'menuSelected': state.menu.selected,
    'permissions': state.session.permissions,
    'goBack': ownProps.goBack,
    'push': ownProps.push,
  };
};

const {fetchShift} = Actions;

export default connect(mapStateToProps, {
  fetchShift,
})(form);
